import { KTSVG } from '../../../_metronic/helpers'
import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { useAuth } from '../../../app/modules/auth'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import Select from 'react-select'
import { useSelectStyles } from '../../helpers/useSelectStyles'
import dayjs from 'dayjs'
import { useUpdatePurchase } from '../../contextProviders/Atomqueryprovider'
import { Column } from 'react-table'
import { Mainsubtable2 } from '../../tables/Mainsubtable2'
import { useWindowWidth } from '@react-hook/window-size/throttled'
import truncate from 'truncate'

const CURRENCY_APP = process.env.REACT_APP_CURRENCY || 'KES'

type Props = {
  show: boolean
  handleClose: () => void
  purchasetoupdate: any
  allpurchasesrefetch: () => void
  sumpurchaserefetch: () => void
  variancerefetch: () => void
}

interface PurchaseItem {
  id: string
  purchase_item_id?: number
  product_id: number
  product_name: string
  quantity: number
  unit_cost: number
  total_cost: number
  received_quantity: number
}

interface PurchaseFormValues {
  purchase_number: string
  supplier_id: number | null
  purchase_order_id: string
  total_amount: number
  amount_paid: number
  payment_terms: string
  payment_status: string
  due_date: string
  purchase_date: string
  received_date: string
  notes: string
  status: string
}

const getInitialValues = (data: any): PurchaseFormValues => {
  if (!data)

    return {
      purchase_number: '',
      supplier_id: null,
      purchase_order_id: '',
      total_amount: 0,
      amount_paid: 0,
      payment_terms: '',
      payment_status: '',
      due_date: '',
      purchase_date: dayjs().format('YYYY-MM-DD'),
      received_date: '',
      notes: '',
      status: '',
    }

  return {
    purchase_number: data.purchase_number || '',
    supplier_id: data.supplier_id || null,
    purchase_order_id: data.purchase_order_id || '',
    total_amount: data.total_amount || 0,
    amount_paid: data.amount_paid || 0,
    payment_terms: data.payment_terms || '',
    payment_status: data.payment_status || '',
    due_date: data.due_date ? dayjs(data.due_date).format('YYYY-MM-DD') : '',
    purchase_date: data.purchase_date
      ? dayjs(data.purchase_date).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD'),
    received_date: data.received_date ? dayjs(data.received_date).format('YYYY-MM-DD') : '',
    notes: data.notes || '',
    status: data.status || '',
  }
}

const purchaseSchema = Yup.object().shape({
  payment_status: Yup.string().required('Payment status is required'),
  status: Yup.string().required('Status is required'),
  amount_paid: Yup.number()
    .min(0, 'Amount paid cannot be negative')
    .when('payment_status', {
      is: 'PAID',
      then: (schema) => schema.oneOf([Yup.ref('total_amount')], 'Amount paid must equal total amount for PAID status'),
    }),
  total_amount: Yup.number().min(1, 'Total amount must be greater than 0'),
})

const addCommas = (num: any) => {
  // Handle undefined, null, or non-numeric values
  if (num === undefined || num === null || num === '') {
    return '0'
  }
  
  // Convert to number if it's a string
  const numValue = typeof num === 'string' ? parseFloat(num) : num
  
  // Check if it's a valid number
  if (isNaN(numValue)) {
    return '0'
  }
  
  return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

const generateId = () => Math.random().toString(36).substr(2, 9)

const paymentStatusOptions = [
  { value: 'OVERDUE', label: 'OVERDUE' },
  { value: 'PARTIAL', label: 'PARTIAL' },
  { value: 'PAID', label: 'PAID' },
  { value: 'CREDIT', label: 'CREDIT' },
]

const statusOptions = [
  { value: 'PENDING', label: 'PENDING' },
  { value: 'RECEIVED', label: 'RECEIVED' },
  { value: 'CANCELLED', label: 'CANCELLED' },
]

export const UpdatePurchase: React.FC<Props> = ({
  show,
  handleClose,
  purchasetoupdate,
  allpurchasesrefetch,
  sumpurchaserefetch,
  variancerefetch,
}) => {
  const [loading, setLoading] = useState(false)
  const [purchaseupdated, setPurchaseupdated] = useState<any>({})
  const [submittingready, setSubmittingready] = useState<boolean>(false)
  const { currentUser } = useAuth()

  const { mutateAsync: updatePurchaseAsync } = useUpdatePurchase()

  const selectStyles = useSelectStyles()
  const [purchaseItems, setPurchaseItems] = useState<PurchaseItem[]>([])
  const [totalAmount, setTotalAmount] = useState<number>(0)
  const onlyWidth = useWindowWidth()
  const isStatusReceived = (status: string) => status === 'RECEIVED'
  
  const formik = useFormik<PurchaseFormValues>({
    initialValues: getInitialValues(purchasetoupdate),
    validationSchema: purchaseSchema,
    validateOnBlur: true,
    onSubmit: async (values, { setStatus, setSubmitting, resetForm }) => {
      setLoading(true)

      // Format received_quantities for backend
      const received_quantities = purchaseItems.map((item) => ({
        purchase_item_id: item.purchase_item_id,
        received_quantity: item.received_quantity,
      }))

      // Format updated_quantities for backend (new field)
      const updated_quantities = purchaseItems.map((item) => ({
        purchase_item_id: item.purchase_item_id,
        quantity: item.quantity,
        total_cost: item.total_cost,
      }))

      // Prepare data in the format your backend expects
      const updateData = {
        supplier_id: values.supplier_id,
        purchase_order_id: values.purchase_order_id,
        total_amount: totalAmount,
        amount_paid: values.amount_paid,
        payment_terms: values.payment_terms,
        payment_status: values.payment_status,
        due_date: values.due_date ? dayjs(values.due_date).format('YYYY-MM-DD') : null,
        purchase_date: dayjs(values.purchase_date).format('YYYY-MM-DD'),
        received_date: values.received_date ? dayjs(values.received_date).format('YYYY-MM-DD') : null,
        notes: values.notes,
        status: values.status,
        received_quantities, // Backend expects this field name
        updated_quantities, // New field for updated purchase quantities
        user_id: Number(currentUser?.user_id), // Required by backend
      }

      setPurchaseupdated({
        purchase_id: purchasetoupdate.purchase_id,
        updateData,
      })

      setSubmittingready(true)
    },
  })

  // Execute mutation in useEffect
  useEffect(() => {
    if (submittingready && purchaseupdated.purchase_id && purchaseupdated.updateData) {
      updatePurchaseAsync(
        {
          purchase_id: purchaseupdated.purchase_id,
          updateData: purchaseupdated.updateData,
        },
        {
          onSuccess: () => {
            setLoading(false)
            formik.resetForm({})
            formik.setSubmitting(false)
            setPurchaseupdated({})
            sumpurchaserefetch()
            allpurchasesrefetch()
            variancerefetch()
            setSubmittingready(false)
            handleClose()
          },
          onError: (error: any) => {
            console.error('Update purchase error:', error)
            formik.setStatus('Error updating purchase')
            formik.setSubmitting(false)
            setLoading(false)
            setSubmittingready(false)
          },
        }
      )
    }
  }, [submittingready, purchaseupdated, updatePurchaseAsync])

  // Initialize purchase items when data is ready
  useEffect(() => {
    if (!purchasetoupdate) return

    const initialItems = (purchasetoupdate.items || []).map((item: any) => {
      return {
        id: generateId(),
        purchase_item_id: item.purchase_item_id,
        product_id: item.product_id,
        product_name: item.product_details?.product_name?.trim() || 'Unknown',
        quantity: item.quantity,
        unit_cost: item.unit_cost,
        total_cost: item.quantity * item.unit_cost,
        received_quantity: item.received_quantity || item.quantity, // Default to full quantity if not set
      }
    })

    setPurchaseItems(initialItems)
  }, [purchasetoupdate])

  // Recalculate total when items change
  useEffect(() => {
    const total = purchaseItems.reduce((sum, item) => sum + item.total_cost, 0)
    setTotalAmount(total)
    formik.setFieldValue('total_amount', total)
  }, [purchaseItems])

  const handleUpdateReceivedQuantity = (id: string, newQuantity: number) => {
    setPurchaseItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === id) {
          const clamped = Math.min(Math.max(0, newQuantity), item.quantity)
          return { ...item, received_quantity: clamped }
        }
        return item
      })
    )
  }

  const handleUpdatePurchaseQuantity = (id: string, newQuantity: number) => {
    setPurchaseItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === id) {
          const clamped = Math.max(1, newQuantity) // Ensure minimum quantity of 1
          const newTotalCost = clamped * item.unit_cost
          return { 
            ...item, 
            quantity: clamped,
            total_cost: newTotalCost,
            // Reset received quantity if it exceeds new purchase quantity
            received_quantity: Math.min(item.received_quantity, clamped)
          }
        }
        return item
      })
    )
  }

  // Desktop Columns
  const itemColumns: ReadonlyArray<Column<PurchaseItem>> = [
    {
      Header: 'Product',
      accessor: 'product_name',
      Cell: ({ value }) => (
        <span className='text-dark text-nowrap fw-bolder d-block mb-1 fs-6' title={value}>
          {truncate(value, 20)}
        </span>
      ),
    },
    {
      Header: 'P.Quantity',
      accessor: 'quantity',
      Cell: ({ value, row }: any) => (
        <input
          type='number'
          value={value}
          onChange={(e) => handleUpdatePurchaseQuantity(row.original.id, Number(e.target.value))}
          className='form-control form-control-solid border border-success'
          min={1}
          style={{ 
            maxWidth: '100px',
            backgroundColor: isStatusReceived(formik.values.status) ? '#d1f2eb' : undefined
          }}
          disabled={isStatusReceived(formik.values.status)}
        />
      ),
    },
    {
      Header: 'U.Cost',
      accessor: 'unit_cost',
      Cell: ({ value }) => (
        <span className='text-dark text-nowrap fw-bolder d-block mb-1 fs-6'>
          {addCommas(Number(value).toFixed(2))} {CURRENCY_APP}
        </span>
      ),
    },
    {
      Header: 'R.Quantity',
      accessor: 'received_quantity',
      Cell: ({ value, row }: any) => (
        <input
          type='number'
          value={value}
          onChange={(e) => handleUpdateReceivedQuantity(row.original.id, Number(e.target.value))}
          className='form-control form-control-solid border border-primary'
          min={0}
          max={row.original.quantity}
          style={{ 
            maxWidth: '100px',
            backgroundColor: isStatusReceived(formik.values.status) ? '#d1f2eb' : undefined
          }}
          disabled={isStatusReceived(formik.values.status)}
        />
      ),
    },
    {
      Header: 'Total',
      accessor: 'total_cost',
      Cell: ({ value }) => (
        <span className='text-dark text-nowrap fw-bolder d-block mb-1 fs-6'>
          {addCommas(Number(value).toFixed(2))} {CURRENCY_APP}
        </span>
      ),
    },
  ]

  // Mobile Columns
  const itemColumnsMobile: ReadonlyArray<Column<PurchaseItem>> = [
    {
      Header: 'Item Details',
      accessor: 'product_name',
      Cell: ({ value, row }) => (
        <>
          <span className='text-dark text-nowrap fw-bolder d-block mb-1 fs-6' title={value}>
            <span>{truncate(value, 15)}</span>
          </span>
          <span className='text-muted text-nowrap d-block mb-1 fs-6'>
            {addCommas(row.original.total_cost.toFixed(2))} {CURRENCY_APP}
          </span>
        </>
      ),
    },
    {
      Header: 'P.Quantity',
      accessor: 'quantity',
      Cell: ({ value, row }: any) => (
        <input
          type='number'
          value={value}
          onChange={(e) => handleUpdatePurchaseQuantity(row.original.id, Number(e.target.value))}
          className='form-control form-control-solid border border-success'
          min={1}
          style={{ 
            maxWidth: '70px', 
            fontSize: '0.875rem',
            backgroundColor: isStatusReceived(formik.values.status) ? '#d1f2eb' : undefined
          }}
          disabled={isStatusReceived(formik.values.status)}
        />
      ),
    },
    {
      Header: 'R.Quantity',
      accessor: 'received_quantity',
      Cell: ({ value, row }: any) => (
        <input
          type='number'
          value={value}
          onChange={(e) => handleUpdateReceivedQuantity(row.original.id, Number(e.target.value))}
          className='form-control form-control-solid border border-primary'
          min={0}
          max={row.original.quantity}
          style={{ 
            maxWidth: '70px', 
            fontSize: '0.875rem',
            backgroundColor: isStatusReceived(formik.values.status) ? '#d1f2eb' : undefined
          }}
          disabled={isStatusReceived(formik.values.status)}
        />
      ),
    },
  ]

  const tableData = purchaseItems

  return (
    <Modal
      className='modal fade'
      id='kt_modal_dashboard'
      data-backdrop='static'
      tabIndex={-1}
      role='dialog'
      show={show}
      centered
      dialogClassName='modal-lg'
      aria-hidden='true'
      onHide={handleClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h3 className='fw-bolder'>
            Update Purchase
            <span className='h-10px border-gray-200 border-start mx-2'></span>
            <span className='text-muted fw-bold fs-6'>{purchasetoupdate?.purchase_number}</span>
          </h3>
          <div className='d-flex align-items-center'>
            <div className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={handleClose}>
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-2x' />
            </div>
          </div>
        </div>
        <div className='modal-body'>
          {formik.status && (
            <div className='alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          )}
          <form
            id='kt_modal_update_purchase_form'
            noValidate
            className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
            onSubmit={formik.handleSubmit}
          >
            {/* Items Table */}
            <div className='fv-row mb-7'>
              <div className='card bg-light'>
                <div className='card-header border-0 pt-5'>
                  <h3 className='card-title align-items-start flex-column'>
                  
                    <span className='required card-label fw-bolder fs-3 mb-1'>Purchase Items</span>
                    <span className='text-muted mt-1 fw-bold fs-7'>
                      {purchaseItems.length === 1
                        ? `${purchaseItems.length} item`
                        : `${purchaseItems.length} items`}
                    </span>
                  </h3>
                </div>
                <div className='card-body py-3'>
                  <div className='table-responsive'>
                    <Mainsubtable2
                      columnaddedtitems={onlyWidth > 500 ? itemColumns : itemColumnsMobile}
                      adddatatable={tableData}
                    />
                  </div>

                  {purchaseItems.length > 0 && (
                    <div className='fv-row mt-5 mb-5'>
                      <div className='d-flex flex-column flex-grow-1'>
                        <div className='d-flex justify-content-end'>
                          <div>
                            <div className='border-top border-bottom border-dark py-3'>
                              <span className='fs-5 fw-bolder me-2 mt-3'>
                                <span className='me-3'>Total Amount:</span>
                                <span className='text-primary'>
                                  {addCommas(totalAmount.toFixed(2))} {CURRENCY_APP}
                                </span>
                              </span>
                            </div>
                            {formik.values.amount_paid > 0 && (
                              <div className='pt-3'>
                                <div className='d-flex justify-content-between mb-2'>
                                  <span className='fw-bold fs-7 text-gray-600'>Amount Paid:</span>
                                  <span className='fw-bold fs-7'>
                                    {addCommas(Number(formik.values.amount_paid).toFixed(2))}{' '}
                                    {CURRENCY_APP}
                                  </span>
                                </div>
                                <div className='d-flex justify-content-between'>
                                  <span className='fw-bold fs-7 text-gray-600'>Balance Due:</span>
                                  <span className='fw-bold fs-7 text-warning'>
                                    {addCommas(
                                      (totalAmount - Number(formik.values.amount_paid)).toFixed(2)
                                    )}{' '}
                                    {CURRENCY_APP}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Status & Status */}
            <div className='row fv-row mb-7'>
              <div className='col-xl-6'>
                <label className='required fw-bold fs-6 mb-2'>Payment Status</label>
                <Select
                  styles={selectStyles}
                  isClearable={true}
                  isSearchable={true}
                  placeholder='Select payment status ...'
                  options={paymentStatusOptions}
                  value={
                    paymentStatusOptions.find((opt) => opt.value === formik.values.payment_status) ||
                    null
                  }
                  onChange={(selected) =>
                    formik.setFieldValue('payment_status', selected ? selected.value : '')
                  }
                  onBlur={() => formik.setFieldTouched('payment_status', true)}
                />
                {formik.touched.payment_status && formik.errors.payment_status && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.payment_status}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className='col-xl-6'>
                <label className='required fw-bold fs-6 mb-2'>Status</label>
                <Select
                  styles={{
                    ...selectStyles,
                    control: (provided, state) => ({
                      ...selectStyles.control?.(provided, state),
                      backgroundColor: isStatusReceived(formik.values.status) ? '#d1f2eb' : provided.backgroundColor,
                      cursor: isStatusReceived(formik.values.status) ? 'not-allowed' : 'default'
                    })
                  }}
                  isClearable={!isStatusReceived(formik.values.status)}
                  isSearchable={!isStatusReceived(formik.values.status)}
                  isDisabled={isStatusReceived(formik.values.status)}
                  placeholder='Select status ...'
                  options={statusOptions}
                  value={statusOptions.find((opt) => opt.value === formik.values.status) || null}
                  onChange={(selected) =>
                    formik.setFieldValue('status', selected ? selected.value : '')
                  }
                  onBlur={() => formik.setFieldTouched('status', true)}
                />
                {formik.touched.status && formik.errors.status && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.status}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Amount Paid & Total Amount */}
            <div className='row fv-row mb-7'>
              <div className='col-xl-6'>
                <label className='fw-bold fs-6 mb-2'>Amount Paid</label>
                <input
                  {...formik.getFieldProps('amount_paid')}
                  type='number'
                  step='0.01'
                  className={clsx(
                    'form-control form-control-solid mb-3 mb-lg-0',
                    {
                      'is-invalid': formik.touched.amount_paid && formik.errors.amount_paid,
                    },
                    {
                      'is-valid': formik.touched.amount_paid && !formik.errors.amount_paid,
                    }
                  )}
                  autoComplete='off'
                />
                {formik.touched.amount_paid && formik.errors.amount_paid && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.amount_paid}</span>
                    </div>
                  </div>
                )}
              </div>
              <div className='col-xl-6'>
                <label className='fw-bold fs-6 mb-2'>Total Amount</label>
                <input
                  type='number'
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  value={formik.values.total_amount}
                  readOnly
                />
                {formik.touched.total_amount && formik.errors.total_amount && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.total_amount}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
        <div className='modal-footer border-0 mt-n5'>
          <button type='button' className='btn btn-light-primary' onClick={handleClose}>
            Cancel
          </button>
          <button
            type='submit'
            form='kt_modal_update_purchase_form'
            className='btn btn-info'
            disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          >
            {!loading && <span className='indicator-label'>Update Purchase</span>}
            {loading && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </div>
    </Modal>
  )
}